import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Film } from "@/types";

interface HeroCarouselProps {
  films: Film[];
}

export default function HeroCarousel({ films }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (films.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % films.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [films.length]);

  if (films.length === 0) return null;

  const film = films[current];

  const go = (index: number) => {
    setCurrent((index + films.length) % films.length);
  };

  return (
    <section className="relative w-full h-[560px] md:h-[680px] overflow-hidden">
      {films.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === current ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title}剧照`}
            title={`${item.title}影视剧照`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/65 to-foreground-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30"></div>
        </div>
      ))}

      <div className="relative z-10 h-full px-4 md:px-6 flex items-center">
        <div key={film.id} className="max-w-2xl w-full animate-fade-in-up">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-foreground-950 text-xs font-label tracking-wide">
            <i className="ri-fire-line"></i>
            精选热播推荐
          </span>
          <h1 className="mt-4 text-sm md:text-base text-background-200 tracking-wide">
            顽皮的女朋友 · 精彩影视内容大全与剧情资讯
          </h1>
          <h2 className="mt-2 font-heading font-bold text-4xl md:text-6xl leading-tight text-background-50">
            {film.title}
          </h2>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur-sm">
              {film.type}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur-sm">
              {film.region}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur-sm">
              {film.year}
            </span>
            {film.genres.slice(0, 2).map((genre) => (
              <span
                key={genre}
                className="px-2.5 py-1 rounded-md bg-primary-500/80 text-background-50 text-xs"
              >
                {genre}
              </span>
            ))}
          </div>
          <p className="mt-5 text-sm md:text-base leading-relaxed text-background-100 line-clamp-3 max-w-xl">
            {film.synopsis}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${film.id}`}
              className="h-11 px-6 inline-flex items-center gap-2 rounded-md bg-accent-500 text-foreground-950 font-heading text-sm tracking-wide hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to="/category"
              className="h-11 px-6 inline-flex items-center gap-2 rounded-md border border-background-50/40 text-background-50 font-heading text-sm tracking-wide hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-layout-grid-line text-lg"></i>
              浏览全部影视
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(current - 1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm hover:bg-background-50/30 transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(current + 1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur-sm hover:bg-background-50/30 transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {films.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setCurrent(index)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === current ? "w-8 bg-accent-500" : "w-3 bg-background-50/50"
            }`}
            aria-label={`切换到第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}